import type { Appointment } from '@medplum/fhirtypes';
import { getDisplayString } from '@medplum/core';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconLayoutSidebarRightExpand } from '@tabler/icons-react';
import { notify } from '@/lib/notify';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { getVideoMeetingUrl } from '@unify/fhir-model/resources/appointment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMedplumSubscriptionRefresh } from '@/hooks/useMedplumSubscriptionRefresh';
import { buildAppointmentByIdSubscriptionCriteria } from '@/lib/fhirRealtimeCriteria';
import {
  executeChimeTelehealth,
  leaveChimeSessionFinal,
  releaseChimeSessionOnUnload,
  retryFinalLeave,
  withTimeout,
  type ChimeJoinResult,
  type RecordingNotStartedReason,
} from '@/lib/telehealth/chime/chimeTelehealthService';
import { useChimeSessionStatus } from '@/lib/telehealth/chime/useChimeSessionStatus';
import {
  admittedGuestEntries,
  guestHandles,
  unlistedRosterGuests,
  waitingBannerText,
  waitingEntries,
  type WaitingEntry,
} from '@/lib/telehealth/chime/chimeWaitingState';
import { patientDisplayLabelFromReference } from '@/pages/appointments/utils/fhirHelpers';
import { useChimeMeetingSession } from '@/lib/telehealth/chime/useChimeMeetingSession';
import { useDevicePreview } from '@/lib/telehealth/chime/useDevicePreview';
import { microphoneJoinState } from '@/lib/telehealth/chime/chimeDevices';
import {
  chimeAttendeeDisplayName,
  guestHandleMatches,
  presentGuests as presentRosterGuests,
} from '@/lib/telehealth/chime/chimeParticipantVisuals';
import {
  CHIME_RECORDER_SETTLE_MS,
  RECORDING_NOT_STARTED_TEXT,
  providerAdmitReady,
  providerRecordingView,
  type ProviderJoinRecording,
} from '@/lib/telehealth/chime/chimeRecording';
import { useChimeRecordingShown } from '@/lib/telehealth/chime/useChimeRecordingShown';
import { actorRefIsAppointmentParticipant } from '@/features/calendar/calendarEventGuards';
import { useCurrentUserPractitioner } from '@/hooks/useCurrentUserPractitioner';
import { ChimeJoinPreview } from './components/ChimeJoinPreview';
import { SessionClinicalPanelContent } from './components/SessionClinicalPanelContent';
import { ChimeMediaStage } from './components/ChimeMediaStage';
import { CallEndedScreen } from './components/CallEndedScreen';
import { RecordingStatusBar } from './components/RecordingStatusBar';

const PANEL_WIDTH = 520;
/** A notes save slower than this on End counts as failed, so the call is not held open. */
const END_NOTES_FLUSH_TIMEOUT_MS = 8_000;
/** Leave waits this long for the bot to confirm (and stop recording) before returning to the appointment anyway. */
const LEAVE_TIMEOUT_MS = 8_000;

/**
 * Checks participation before mounting the call room, whose device preview prompts for
 * camera access — a non-participant would otherwise grant it only to hit a bot error.
 */
export function ChimeCallPage(): JSX.Element {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const medplum = useMedplum();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  // undefined while loading; null when the Appointment could not be read.
  const [participants, setParticipants] = useState<Appointment['participant'] | null>();
  // Bumped by Retry after a failed read.
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    if (!appointmentId) return undefined;
    let cancelled = false;
    setParticipants(undefined);
    medplum.readResource('Appointment', appointmentId).then(
      (appt) => !cancelled && setParticipants(appt.participant ?? []),
      () => !cancelled && setParticipants(null),
    );
    return () => {
      cancelled = true;
    };
  }, [appointmentId, loadAttempt, medplum]);

  if (appointmentId && (practitionerLoading || participants === undefined)) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  // A failed read is not "not a participant": say it could not load and offer Retry.
  if (appointmentId && participants === null) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="sm" maw={400}>
          <Text c="dimmed" ta="center">
            Could not load this appointment. Check your connection, then try again.
          </Text>
          <Button variant="light" onClick={() => setLoadAttempt((n) => n + 1)}>
            Retry
          </Button>
        </Stack>
      </Center>
    );
  }

  if (
    appointmentId &&
    !actorRefIsAppointmentParticipant(
      participants ?? undefined,
      practitioner?.id ? `Practitioner/${practitioner.id}` : undefined,
    )
  ) {
    return (
      <Center h="100vh">
        <Text c="dimmed" ta="center" maw={400}>
          You are not a participant on this meeting, so the video session is not available.
        </Text>
      </Center>
    );
  }

  return <ChimeCallRoom />;
}

function ChimeCallRoom(): JSX.Element {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const navigate = useNavigate();
  const preview = useDevicePreview();
  const session = useChimeMeetingSession();
  const [appointment, setAppointment] = useState<Appointment | undefined>();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [ended, setEnded] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  const [confirmRecordingOpen, setConfirmRecordingOpen] = useState(false);
  // The sidebar notes flush rejected while ending, leaving, or closing the panel.
  const [notesFailedOn, setNotesFailedOn] = useState<'end' | 'leave' | 'close' | null>(null);
  // Optional Mark as client per waiting plain-link guest: guest handle -> `Patient/<id>`.
  const [markAsClient, setMarkAsClient] = useState<Record<string, string>>({});
  const flushRef = useRef<(() => Promise<void>) | null>(null);
  const aliveRef = useRef(true);
  // Our Chime attendee from `provider-join`, until we have told the bot we left.
  const joinedAttendeeIdRef = useRef<string | undefined>();
  // What our `provider-join` said about recording: gates Admit and drives the not-started message.
  const [joinRecording, setJoinRecording] = useState<ProviderJoinRecording | undefined>();
  const [notStarted, setNotStarted] = useState<RecordingNotStartedReason | undefined>();
  const [recorderSeen, setRecorderSeen] = useState(false);
  const [admitWaitTimedOut, setAdmitWaitTimedOut] = useState(false);
  // Full handles of guests seen in the waiting list; the roster carries only a prefix, and Remove needs the handle.
  const knownGuestHandlesRef = useRef<string[]>([]);

  const reload = useCallback(async () => {
    if (!appointmentId) return;
    try {
      const appt = await medplum.readResource('Appointment', appointmentId, { cache: 'no-cache' });
      setAppointment(appt);
    } catch {
      setAppointment(undefined);
    }
  }, [appointmentId, medplum]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const criteria = useMemo(
    () => (appointmentId ? buildAppointmentByIdSubscriptionCriteria(appointmentId) : undefined),
    [appointmentId],
  );
  useMedplumSubscriptionRefresh(criteria, () => void reload(), { enabled: Boolean(appointmentId) });

  const chartLabelForPatientRef = useCallback(
    (patientRef: string) => {
      const actor = appointment?.participant?.find((p) => p.actor?.reference === patientRef)?.actor;
      return patientDisplayLabelFromReference(patientRef, actor?.display);
    },
    [appointment],
  );

  const inMeeting = session.status === 'in-meeting';
  // Never start with the SDK's silent microphone: wait for the prompt, and require a device.
  const microphone = microphoneJoinState(preview.access, preview.audioDeviceId);
  const {
    status: recordingStatus,
    setStatus: setRecordingStatus,
    refresh: refreshSessionStatus,
  } = useChimeSessionStatus(
    medplum,
    appointmentId,
    inMeeting,
  );
  // The waiting room comes from each 5s `session-status` poll (staff cannot read waiting-room Tasks).
  const waiting = waitingEntries(recordingStatus?.waiting, chartLabelForPatientRef);
  const admittedGuests = admittedGuestEntries(recordingStatus?.waiting);
  const banner = waitingBannerText(waiting.length);
  const clientOptions = (appointment?.participant ?? []).flatMap((p) =>
    p.actor?.reference?.startsWith('Patient/')
      ? [{ value: p.actor.reference, label: chartLabelForPatientRef(p.actor.reference) }]
      : [],
  );
  // Recording comes from Chime's roster (Decision 39): recording while the recorder attendee is present.
  const { recording: recordingActive, expect: expectRecording } = useChimeRecordingShown(
    session.recorderPresent,
    recordingStatus?.recordingActive,
  );
  const recordingView = providerRecordingView({
    recording: recordingActive,
    notStarted,
    canResume: recordingStatus?.canResume,
  });
  useEffect(() => {
    if (session.recorderPresent) setRecorderSeen(true);
  }, [session.recorderPresent]);
  // A not-started reason is a one-off: once recording runs (Retry, or another provider's Resume) it no longer applies.
  useEffect(() => {
    if (recordingActive) setNotStarted(undefined);
  }, [recordingActive]);
  // No admission gap: when our join started the capture, Admit waits for the recorder (or the safety timeout).
  const awaitingRecorder = inMeeting && joinRecording?.captureStartedByThisJoin === true && !recorderSeen;
  useEffect(() => {
    if (!awaitingRecorder) return undefined;
    const timer = setTimeout(() => setAdmitWaitTimedOut(true), CHIME_RECORDER_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [awaitingRecorder]);
  const admitReady = providerAdmitReady({ join: joinRecording, recorderSeen, timedOut: admitWaitTimedOut });
  const othersPresent = Object.keys(session.attendeeExternalUserIds).some(
    (id) => id !== session.attendeeId,
  );
  const rosterGuests = presentRosterGuests(Object.values(session.attendeeExternalUserIds));
  knownGuestHandlesRef.current = [
    ...new Set([...knownGuestHandlesRef.current, ...guestHandles(recordingStatus?.waiting)]),
  ];
  // Guests in the call with no admitted row (in the call already, or a failed or racing removal), named by Chime.
  const unlistedGuests = unlistedRosterGuests(
    rosterGuests,
    admittedGuests.map((entry) => entry.guestId),
    knownGuestHandlesRef.current,
  );
  const guestInCall = (handle: string): boolean =>
    rosterGuests.some((guest) => guestHandleMatches(handle, guest.handlePrefix));

  /**
   * Tell the bot our attendee left, without `final`. Only the explicit End button ends the meeting
   * for everyone and only the explicit Leave stops recording; closing the tab, navigating away, a
   * dropped connection or a failed local join must do neither (the provider may rejoin), but the
   * server still needs `leave` to drop the attendee. Uses the keepalive helper so it also survives
   * tab close.
   */
  const releaseJoin = useCallback(() => {
    const attendeeId = joinedAttendeeIdRef.current;
    joinedAttendeeIdRef.current = undefined;
    if (attendeeId && appointmentId) {
      releaseChimeSessionOnUnload(medplum, { appointmentId, attendeeId });
    }
  }, [appointmentId, medplum]);

  useEffect(() => {
    aliveRef.current = true;
    window.addEventListener('pagehide', releaseJoin);
    return () => {
      aliveRef.current = false;
      window.removeEventListener('pagehide', releaseJoin);
      releaseJoin();
    };
  }, [releaseJoin]);

  // The connection dropped mid-call.
  useEffect(() => {
    if (session.status === 'error') releaseJoin();
  }, [releaseJoin, session.status]);

  const handleStart = useCallback(async () => {
    if (!appointmentId) return;
    setBusy(true);
    setActionError(null);
    await preview.stopPreview();
    if (!aliveRef.current) return;
    try {
      const result = (await executeChimeTelehealth(medplum, {
        action: 'provider-join',
        appointmentId,
      })) as ChimeJoinResult;
      if (!result.meeting || !result.attendee) {
        throw new Error('Chime join did not return meeting credentials');
      }
      joinedAttendeeIdRef.current = result.attendee.AttendeeId;
      // Until media connects and the roster shows the recorder, the join's answer is what we show.
      setRecordingStatus({ recordingActive: Boolean(result.recordingActive) });
      expectRecording(result.recordingActive ? true : undefined);
      setRecorderSeen(false);
      setAdmitWaitTimedOut(false);
      setJoinRecording({
        captureStartedByThisJoin: result.captureStartedByThisJoin,
        recordingNotStarted: result.recordingNotStarted,
      });
      setNotStarted(result.recordingNotStarted);
      if (result.recordingNotStarted) {
        notify({ type: 'warning', message: RECORDING_NOT_STARTED_TEXT[result.recordingNotStarted] });
      }
      if (!aliveRef.current) {
        releaseJoin();
        return;
      }
      const joined = await session.start(result.meeting, result.attendee, {
        audioDeviceId: preview.audioDeviceId,
        videoDeviceId: preview.videoDeviceId,
        requireAudio: true,
        startMuted: !preview.micOn,
        cameraOff: !preview.cameraOn,
      });
      // Unmount's cleanup already released; a failed local join must not leave recording on.
      if (!aliveRef.current) return;
      if (!joined) {
        releaseJoin();
        preview.restartPreview();
      }
      await reload();
    } catch (e) {
      if (aliveRef.current) {
        setActionError(e instanceof Error ? e.message : 'Could not start the session');
        // The preview was stopped for the join; without this it stays black.
        preview.restartPreview();
      }
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  }, [appointmentId, expectRecording, medplum, preview, releaseJoin, reload, session, setRecordingStatus]);

  /** Runs one bot action from the call UI, then reloads the Appointment it changed. */
  const runAction = useCallback(
    async (
      input: Parameters<typeof executeChimeTelehealth>[1],
      failure: string,
    ): Promise<Awaited<ReturnType<typeof executeChimeTelehealth>> | undefined> => {
      setBusy(true);
      setActionError(null);
      try {
        const result = await executeChimeTelehealth(medplum, input);
        await reload();
        return result;
      } catch (e) {
        setActionError(e instanceof Error ? e.message : failure);
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [medplum, reload],
  );

  /** One click. A plain-link guest goes in as Guest unless the provider chose Mark as client. */
  const handleAdmit = useCallback(
    (entry: WaitingEntry) =>
      runAction(
        {
          action: 'admit',
          appointmentId,
          ...(entry.kind === 'guest'
            ? {
                guestId: entry.guestId,
                ...(!entry.participant && markAsClient[entry.guestId]
                  ? { markAsClient: markAsClient[entry.guestId] }
                  : {}),
              }
            : { patientId: entry.patientRef.replace(/^Patient\//, '') }),
        },
        'Could not admit the client',
      ).then(refreshSessionStatus),
    [appointmentId, markAsClient, refreshSessionStatus, runAction],
  );

  const handleRemoveGuest = useCallback(
    (guestId: string) =>
      runAction({ action: 'remove-guest', appointmentId, guestId }, 'Could not remove the guest').then(
        refreshSessionStatus,
      ),
    [appointmentId, refreshSessionStatus, runAction],
  );

  /**
   * Resume or Retry (`recording-start`): capture starts at once; the badge shows recording until the recorder joins the
   * roster. A refusal keeps the current control and shows the bot's reason.
   */
  const handleResumeRecording = useCallback(async () => {
    setConfirmRecordingOpen(false);
    const result = await runAction({ action: 'recording-start', appointmentId }, 'Could not start recording');
    if (result && 'recordingActive' in result && result.recordingActive) {
      setRecordingStatus({ recordingActive: true });
      expectRecording(true);
    }
  }, [appointmentId, expectRecording, runAction, setRecordingStatus]);

  /** Stop: the call continues; a capture ran, so Resume is offered at once (the next poll confirms `canResume`). */
  const handleStopRecording = useCallback(async () => {
    const result = await runAction({ action: 'recording-stop', appointmentId }, 'Could not stop recording');
    if (result && 'recordingActive' in result && !result.recordingActive) {
      setRecordingStatus({ recordingActive: false, canResume: true });
      expectRecording(false);
    }
  }, [appointmentId, expectRecording, runAction, setRecordingStatus]);

  /**
   * Saves the sidebar notes before the provider hangs up. A failed (or hung) save asks before
   * discarding them; the call keeps running meanwhile.
   */
  const notesSavedBefore = useCallback(async (kind: 'end' | 'leave'): Promise<boolean> => {
    try {
      const flush = flushRef.current?.();
      if (flush) await withTimeout(flush, END_NOTES_FLUSH_TIMEOUT_MS, 'Notes save timed out');
      return true;
    } catch {
      if (aliveRef.current) {
        setNotesFailedOn(kind);
        setBusy(false);
      }
      return false;
    }
  }, []);

  /**
   * End session for everyone: `end` at once, with no further confirmation (the hang-up menu is
   * the first click). Local media stops first so the mic and camera go dark immediately; a failed
   * `end` offers a retry rather than leaving the meeting (and recording) running behind a closed page.
   */
  const handleEnd = useCallback(
    async (skipNotes = false) => {
      if (!appointmentId) return;
      setBusy(true);
      setEndError(null);
      if (!skipNotes && !(await notesSavedBefore('end'))) return;
      setNotesFailedOn(null);
      // Keeps the device preview from reopening the camera before the route changes.
      setEnded(true);
      session.stop();
      try {
        await executeChimeTelehealth(medplum, { action: 'end', appointmentId });
      } catch (e) {
        if (aliveRef.current) {
          setEndError(e instanceof Error ? e.message : 'Could not end the session');
          setBusy(false);
        }
        return;
      }
      joinedAttendeeIdRef.current = undefined;
      setBusy(false);
      // Explicit, not navigate(-1): a call opened in a fresh tab has no history to go back to.
      navigate(`/dashboard/appointments/${appointmentId}`);
    },
    [appointmentId, medplum, navigate, notesSavedBefore, session],
  );

  /**
   * The provider's explicit Leave (also "Leave without ending" when `end` keeps failing): `leave` with `final: true`,
   * so the bot stops recording when no other provider remains (Decision 39). Unload and drops send no `final`. If the
   * bot cannot be reached, the attendee is still released (best effort) and the page returns to the appointment.
   */
  const leaveWithoutEnding = useCallback(async () => {
    const attendeeId = joinedAttendeeIdRef.current;
    // Cleared first, so the unmount/pagehide release does not send a second (non-final) leave.
    joinedAttendeeIdRef.current = undefined;
    if (appointmentId) {
      try {
        await withTimeout(
          leaveChimeSessionFinal(medplum, { appointmentId, attendeeId }),
          LEAVE_TIMEOUT_MS,
          'Leave timed out',
        );
      } catch {
        // Retry the explicit Leave (with `final`) as a keepalive request, so recording still stops if it can.
        retryFinalLeave(medplum, { appointmentId, attendeeId });
        notify({
          type: 'warning',
          message: 'Could not confirm that you left the call. Recording may continue until the session ends.',
        });
      }
    }
    navigate(`/dashboard/appointments/${appointmentId}`);
  }, [appointmentId, medplum, navigate]);

  /**
   * Leave call: only our attendee leaves (`leave`, final); the meeting stays open for everyone else, and
   * the bot stops recording when no other provider remains. The provider can rejoin from the
   * appointment.
   */
  const handleLeave = useCallback(
    async (skipNotes = false) => {
      setBusy(true);
      if (!skipNotes && !(await notesSavedBefore('leave'))) return;
      setNotesFailedOn(null);
      setEnded(true);
      session.stop();
      await leaveWithoutEnding();
    },
    [leaveWithoutEnding, notesSavedBefore, session],
  );

  const registerFlush = useCallback((flush: (() => Promise<void>) | null) => {
    flushRef.current = flush;
  }, []);

  const closePanel = useCallback(async (skipNotes = false) => {
    if (!skipNotes) {
      try {
        await flushRef.current?.();
      } catch {
        setNotesFailedOn('close');
        return;
      }
    }
    setNotesFailedOn(null);
    setPanelOpen(false);
  }, []);

  const handleCopyLink = useCallback(async () => {
    // The plain appointment link, with no participant code (Decision 27).
    const link = appointment ? getVideoMeetingUrl(appointment) : undefined;
    if (!link) {
      notify({ type: 'error', message: 'No meeting link is available yet' });
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      notify({ type: 'success', message: 'Meeting link copied' });
    } catch {
      notify({ type: 'error', message: 'Could not copy meeting link' });
    }
  }, [appointment]);

  if (!appointmentId) {
    return (
      <Center h="100vh">
        <Text c="dimmed">No appointment specified.</Text>
      </Center>
    );
  }

  if (ended) {
    return (
      <Center h="100vh">
        {endError ? (
          <Stack align="center" gap="md" maw={400}>
            <Text c="red" ta="center">
              The session could not be ended: {endError}
            </Text>
            <Group>
              <Button variant="default" disabled={busy} onClick={() => void leaveWithoutEnding()}>
                Leave without ending
              </Button>
              <Button loading={busy} onClick={() => void handleEnd(true)}>
                Retry
              </Button>
            </Group>
          </Stack>
        ) : (
          <Loader size="lg" />
        )}
      </Center>
    );
  }

  // Admitted guests and guests already in the call live in the sidebar, so the waiting box can close.
  const guestsSection =
    admittedGuests.length > 0 || unlistedGuests.length > 0 ? (
      <Stack gap="xs">
        <Text fw={600} size="sm">
          Guests
        </Text>
        {admittedGuests.map((entry) => (
          <Group key={entry.guestId} justify="space-between">
            <Text size="sm">
              {entry.label}
              {guestInCall(entry.guestId) ? ' · in session' : ''}
            </Text>
            <Button
              size="xs"
              variant="default"
              loading={busy}
              onClick={() => void handleRemoveGuest(entry.guestId)}
            >
              Remove
            </Button>
          </Group>
        ))}
        {unlistedGuests.map((guest) => (
          <Group key={guest.handlePrefix} justify="space-between">
            <Text size="sm">{guest.name} · in session</Text>
            <Button size="xs" variant="default" loading={busy} onClick={() => void handleRemoveGuest(guest.handle)}>
              Remove
            </Button>
          </Group>
        ))}
      </Stack>
    ) : undefined;

  // Another provider ended the meeting; do not offer to start it again.
  if (session.endedByHost) {
    return (
      <CallEndedScreen
        message="This session was ended by another provider."
        doneLabel="Back to appointment"
        onDone={() => navigate(`/dashboard/appointments/${appointmentId}`)}
      />
    );
  }

  return (
    <Box
      h="100dvh"
      style={{ display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}
    >
      {banner && inMeeting ? (
        <Alert
          color="yellow"
          // `light` (the default) tints with a translucent background, which the dark video
          // stage shows straight through. `white` keeps it a solid, readable card.
          variant="white"
          title={banner}
          style={{
            position: 'absolute',
            zIndex: 10,
            top: 16,
            left: '50%',
            width: 'min(480px, calc(100% - 32px))',
            transform: 'translateX(-50%)',
            boxShadow: 'var(--mantine-shadow-lg)',
          }}
        >
          <Stack gap="xs">
            {waiting.length > 0 && !admitReady ? (
              <Text size="xs" c="dimmed">
                Starting recording…
              </Text>
            ) : null}
            {waiting.map((entry) => (
              <Group key={entry.kind === 'guest' ? entry.guestId : entry.patientRef} justify="space-between">
                <Text size="sm">{entry.label}</Text>
                <Group gap="xs">
                  {entry.kind === 'guest' && !entry.participant && clientOptions.length > 0 ? (
                    <Select
                      size="xs"
                      w={160}
                      placeholder="Mark as client"
                      aria-label="Mark as client"
                      clearable
                      data={clientOptions}
                      value={markAsClient[entry.guestId] || null}
                      onChange={(value) =>
                        setMarkAsClient((current) => ({ ...current, [entry.guestId]: value ?? '' }))
                      }
                      comboboxProps={{ withinPortal: false }}
                    />
                  ) : null}
                  <Button
                    size="xs"
                    loading={busy}
                    disabled={!admitReady}
                    onClick={() => void handleAdmit(entry)}
                  >
                    Admit
                  </Button>
                  {entry.kind === 'guest' ? (
                    <Button
                      size="xs"
                      variant="default"
                      loading={busy}
                      onClick={() => void handleRemoveGuest(entry.guestId)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Group>
              </Group>
            ))}
          </Stack>
        </Alert>
      ) : null}

      {actionError || session.error || preview.error ? (
        <Text c="red" px="md" pt="sm">
          {actionError ?? session.error ?? preview.error}
        </Text>
      ) : null}

      {inMeeting ? (
        <RecordingStatusBar
          view={recordingView}
          busy={busy}
          onStop={() => void handleStopRecording()}
          onResume={() =>
            othersPresent ? setConfirmRecordingOpen(true) : void handleResumeRecording()
          }
        />
      ) : null}

      <Modal
        opened={confirmRecordingOpen}
        onClose={() => setConfirmRecordingOpen(false)}
        title="Start recording?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">Recording starts at once for everyone in the call.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmRecordingOpen(false)}>
              Cancel
            </Button>
            <Button color="red" loading={busy} onClick={() => void handleResumeRecording()}>
              Resume recording
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={notesFailedOn !== null}
        onClose={() => setNotesFailedOn(null)}
        title="Notes didn't save"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Your session notes have not saved yet. Retry to wait for it, or{' '}
            {notesFailedOn === 'end'
              ? 'end the session'
              : notesFailedOn === 'leave'
                ? 'leave the call'
                : 'close the sidebar'}{' '}
            without waiting —
            the pending save keeps trying in the background and may finish after you leave, or
            may not finish at all.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              color="red"
              disabled={busy}
              onClick={() =>
                notesFailedOn === 'end'
                  ? void handleEnd(true)
                  : notesFailedOn === 'leave'
                    ? void handleLeave(true)
                    : void closePanel(true)
              }
            >
              {notesFailedOn === 'end'
                ? 'End without waiting'
                : notesFailedOn === 'leave'
                  ? 'Leave without waiting'
                  : 'Close without waiting'}
            </Button>
            <Button
              loading={busy}
              onClick={() =>
                notesFailedOn === 'end'
                  ? void handleEnd()
                  : notesFailedOn === 'leave'
                    ? void handleLeave()
                    : void closePanel()
              }
            >
              Retry
            </Button>
          </Group>
        </Stack>
      </Modal>

      {!inMeeting ? (
        <Center p="md" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Stack align="center" gap="md" maw={600}>
            <Text size="lg" fw={500}>
              Preview your camera
            </Text>
            <ChimeJoinPreview preview={preview} />
            {preview.access === 'blocked' ? (
              <Button variant="light" onClick={preview.requestAccess}>
                Allow camera &amp; microphone
              </Button>
            ) : null}
            <Select
              label="Microphone"
              data={preview.audioDevices.map((d) => ({ value: d.deviceId, label: d.label }))}
              value={preview.audioDeviceId}
              onChange={(v) => v && preview.setAudioDeviceId(v)}
              disabled={preview.audioDevices.length === 0}
              placeholder={preview.audioDevices.length === 0 ? 'No microphone detected' : undefined}
              w="100%"
            />
            <Select
              label="Camera"
              data={preview.videoDevices.map((d) => ({ value: d.deviceId, label: d.label }))}
              value={preview.videoDeviceId}
              onChange={(v) => v && preview.setVideoDeviceId(v)}
              disabled={preview.videoDevices.length === 0}
              placeholder={preview.videoDevices.length === 0 ? 'No camera detected' : undefined}
              w="100%"
            />
            {microphone.notice ? (
              <Text c="red" size="sm" ta="center">
                {microphone.notice}
              </Text>
            ) : null}
            <Button
              size="lg"
              loading={busy}
              disabled={microphone.disabled}
              onClick={() => void handleStart()}
            >
              Start session
            </Button>
          </Stack>
        </Center>
      ) : (
        <ChimeMediaStage
          tiles={session.tiles}
          bindTile={session.bindTile}
          bindAudio={session.bindAudio}
          muted={session.muted}
          cameraOn={session.cameraOn}
          onToggleMute={session.toggleMute}
          onToggleCamera={() => void session.toggleCamera()}
          onEnd={() => void handleLeave()}
          onEndForEveryone={() => void handleEnd()}
          endDisabled={busy}
          busy={busy}
          activeSpeakerId={session.activeSpeakerId}
          attendeeExternalUserIds={session.attendeeExternalUserIds}
          localDisplayName={(profile ? getDisplayString(profile) : '') || 'Provider'}
          displayNameForExternalUserId={chimeAttendeeDisplayName}
          onCopyLink={() => void handleCopyLink()}
          recording={recordingActive}
          recordingDetail={recordingActive ? undefined : recordingView.label}
          screenShare={{
            sharing: session.sharing,
            onStart: () => void session.startScreenShare(),
            onStop: session.stopScreenShare,
          }}
          localAttendeeId={session.attendeeId}
        />
      )}

      {inMeeting ? (
        <>
          {panelOpen ? (
            <Box
              pos="fixed"
              top={16}
              right={16}
              bottom={16}
              w={PANEL_WIDTH}
              bg="white"
              style={{
                zIndex: 20,
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2)',
                borderRadius: 'var(--mantine-radius-sm)',
                overflow: 'hidden',
              }}
            >
              <SessionClinicalPanelContent
                appointmentId={appointmentId}
                onClose={() => void closePanel()}
                onRegisterFlush={registerFlush}
                guestsSection={guestsSection}
              />
            </Box>
          ) : (
            <Tooltip label="Open clinical sidebar" position="left" withArrow>
              <ActionIcon
                variant="filled"
                color="gold"
                size={44}
                radius="md"
                aria-label="Open clinical sidebar"
                onClick={() => setPanelOpen(true)}
                style={{
                  position: 'fixed',
                  top: '50%',
                  right: 0,
                  transform: 'translateY(-50%)',
                  zIndex: 20,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                <IconLayoutSidebarRightExpand size={22} />
              </ActionIcon>
            </Tooltip>
          )}
        </>
      ) : null}
    </Box>
  );
}
